// The account language and the app's own words. The language is chosen once (first run or the
// bot's question), stored on the server, and decides:
//   • the app's words, in one language only: Tamil, English, Hindi, Telugu, Kannada or Malayalam
//     (Tanglish reads the app in English);
//   • the default script for recipe text (script.ts). Recipes are written in Tamil, English and
//     Tanglish only, so Hindi, Telugu, Kannada and Malayalam readers get recipe text in English.
import { useEffect, useState } from 'preact/hooks';
import { load, save, KEYS } from './storage';
import { setScript } from './script';
import type { Lang, Script } from './types';
import { hi } from './locales/hi';
import { te } from './locales/te';
import { kn } from './locales/kn';
import { ml } from './locales/ml';

export type { Lang };
export type UI = 'ta' | 'en' | 'hi' | 'te' | 'kn' | 'ml';
const LANGS: Lang[] = ['ta', 'en', 'tg', 'hi', 'te', 'kn', 'ml'];

/** The script recipe text is shown in for a language. */
export function contentScript(l: Lang): Script {
  return l === 'ta' || l === 'tg' ? l : 'en';
}

const listeners = new Set<() => void>();

/** The chosen language, or '' when the user has not chosen yet. */
export function getLang(): Lang | '' {
  const l = load<string>(KEYS.lang, '') as Lang;
  return LANGS.includes(l) ? l : '';
}

/** Applies a language locally: app words, default recipe script. Saving to the server is the caller's job. */
export function applyLang(l: Lang) {
  save(KEYS.lang, l);
  setScript(contentScript(l));
  document.documentElement.lang = uiOf(l);
  listeners.forEach((fn) => fn());
}

function uiOf(l: Lang): UI {
  return l === 'tg' ? 'en' : l;
}

/** The language of the app's words: the chosen one, else the browser's when we speak it. */
export function uiLang(): UI {
  const l = getLang();
  if (l) return uiOf(l);
  try {
    const b = navigator.language.toLowerCase().slice(0, 2) as Lang;
    return LANGS.includes(b) ? uiOf(b) : 'en';
  } catch {
    return 'en';
  }
}

/** The BCP 47 locale for dates in the UI language. */
function locale(): string {
  return `${uiLang()}-IN`;
}

const en = {
  // shell
  'nav.main': 'Main',
  'nav.home': 'Home',
  'nav.albums': 'Albums',
  'nav.grocery': 'Grocery',
  'nav.home.label': 'Eat Recipe home',
  'common.tryAgain': 'Try again',
  'common.tryingAgain': 'Trying again…',
  'common.back': 'Back',
  'common.cancel': 'Cancel',
  'common.save': 'Save',
  'common.done': 'Done',
  'common.loading': 'Loading',
  'common.seeAll': 'See all',
  'common.addReel': 'Add reel',
  'common.profile': 'Settings and profile',
  'common.watchReel': 'Watch reel ↗',
  'common.veg': 'Veg',
  'common.nonveg': 'Non-veg',
  'common.unsure': 'Not sure',
  'n.recipes_one': '{n} recipe',
  'n.recipes': '{n} recipes',
  'n.ingredients_one': '{n} ingredient',
  'n.ingredients': '{n} ingredients',
  'n.steps_one': '{n} step',
  'n.steps': '{n} steps',
  'n.cooked': 'Cooked {n}×',
  'n.cookedOn': 'Cooked {n}× · {date}',
  'verdict.again': 'Make again',
  'verdict.ok': 'It was okay',
  'verdict.no': 'Not for us',

  // language
  'lang.title': 'Choose your language',
  'lang.body': 'Recipes, buttons and the Instagram bot’s replies follow this. Change it any time in Settings.',
  'lang.ta': 'Recipes and the app in Tamil',
  'lang.en': 'Recipes and the app in English',
  'lang.tg': 'Names like thakkali, vengayam · app in English',
  'lang.continue': 'Continue',
  'lang.saveFailed': 'Couldn’t save your language. Check your connection and try again.',

  // home
  'greet.tiffin': 'Morning{name} · tiffin time',
  'greet.lunch': 'Afternoon{name} · lunch time',
  'greet.teatime': 'Evening{name} · tea-time',
  'greet.dinner': 'Evening{name} · dinner time',
  'greet.latenight': 'Late night{name} · something quick?',
  'home.title': 'What are you craving?',
  'home.search': 'Search {n} recipes · tomato · rasam',
  'home.searchEmpty': 'Search · tomato · rasam',
  'home.mood': 'In the mood for',
  'home.surprise': 'Can’t decide? Surprise me',
  'home.howAbout': 'How about…',
  'home.cookThis': 'Let’s cook this',
  'home.another': 'Another',
  'home.cookAgain': 'Cook again',
  'home.cookAgainHint': 'You said “make again”',
  'home.yourAlbums': 'Your albums',
  'home.justSaved': 'Just saved',
  'home.allByDate': 'All by date',
  'home.readingFrom': 'Reading a reel from {who}',
  'home.reading': 'Reading a reel',
  'home.landsInAlbums': 'Lands in the right albums when ready',
  'home.addedTo': 'Added to {albums}',
  'home.noRecipe': 'No recipe found in this reel',
  'home.failed': 'Couldn’t read this reel · tap to try again',
  'home.newTitle': 'Your recipes land here, sorted by craving',
  'home.newBody': 'In Instagram, tap ➤ on any cooking reel and send it to @eat.recipe. A minute later it’s here — in Tamil, English and Tanglish.',
  'home.openInstagram': 'Message @eat.recipe',
  'home.pasteLink': 'Paste a reel link instead',
  'reason.never_tried': 'saved, never tried',
  'reason.make_again_days': 'you said make again · {n} days ago',
  'reason.make_again_weeks': 'you said make again · {n} weeks ago',
  'reason.not_cooked_days': 'not cooked in {n} days',
  'reason.not_cooked_weeks': 'not cooked in {n} weeks',
  'reason.meal.tiffin': 'good for tiffin',
  'reason.meal.lunch': 'good for lunch',
  'reason.meal.teatime': 'good with tea',
  'reason.meal.dinner': 'good for dinner',
  'reason.meal.latenight': 'quick for a late night',
  'surprise.none': 'Nothing to suggest here yet.',

  // albums
  'albums.title': 'Albums',
  'albums.sub': '{n} recipes, sorted for you as they arrive',
  'albums.sub_one': '1 recipe, sorted for you as it arrives',
  'albums.search': 'Search all recipes',
  'albums.mine': 'Made by you',
  'albums.new': 'New album',
  'albums.browse': 'Browse by',
  'albums.view': 'Album view',
  'view.craving': 'Craving',
  'view.region': 'Region',
  'view.meal': 'Meal',
  'view.timeline': 'Timeline',
  'hint.craving': 'Pick by how you feel — spicy, comforting, quick or light.',
  'hint.region': 'Taste of a place — from Chettinad kitchens to global plates.',
  'hint.meal': 'What time is it? Tiffin to dinner, plus the lunchbox.',
  'hint.timeline': 'Every reel you shared, by when you saved it.',
  'albums.empty': 'Nothing here yet. Share a few reels to @eat.recipe and they’ll sort themselves into albums.',
  'tl.week': 'This week',
  'tl.month_rest': 'Earlier this month',
  'tl.before': 'Before that',
  'tl.reading': '{n} reading…',

  // album
  'kind.craving': 'Craving',
  'kind.region': 'Region',
  'kind.meal': 'Meal',
  'kind.mine': 'Your album',
  'album.stats': '{n} recipes · {c} cooked · {u} not tried yet',
  'album.stats_one': '1 recipe · {c} cooked · {u} not tried yet',
  'album.pin': 'Pin to home',
  'album.unpin': 'Unpin from home',
  'album.pinned': 'Pinned to Home',
  'album.unpinned': 'Removed from Home',
  'album.backToAlbums': 'Back to albums',
  'filter.label': 'Filter recipes',
  'filter.all': 'All',
  'filter.untried': 'Not tried yet',
  'album.pick': 'Pick one from here for me',
  'album.favourites': 'Your favourites',
  'album.notTried': 'Not tried yet',
  'album.cookedBefore': 'Cooked before',
  'album.saved': 'saved {date}',
  'album.shop': 'Shop for a few of these',
  'album.shopTitle': 'Which ones are you cooking?',
  'album.shopGo': 'Make grocery list · {n}',
  'album.empty': 'No recipes here yet.',
  'album.emptyMine': 'Open a recipe and tap “Add to album” to file it here.',
  'album.noMatch': 'Nothing matches this filter.',
  'album.showAll': 'Show all',
  'album.rename': 'Rename',
  'album.delete': 'Delete album',
  'album.deleteConfirm': 'Tap again to delete — the recipes stay in your library',
  'album.notFound': 'This album doesn’t exist any more.',
  'album.nameLabel': 'Album name',
  'album.namePlaceholder': 'Amma’s style · Sunday special',
  'album.create': 'Create album',
  'album.created': 'Album created',

  // timeline (the old library list)
  'timeline.title': 'All by date',
  'timeline.newest': 'Newest first',
  'timeline.search': 'Search recipes',
  'timeline.empty': 'Your library is empty',
  'timeline.emptyBody': 'In Instagram, tap ➤ on any cooking reel and send it to @eat.recipe. The recipe appears here in seconds — in Tamil, English and Tanglish.',
  'timeline.noMatch': 'Nothing matches this filter.',
  'timeline.showAll': 'Show all',
  'timeline.retrying': 'Trying again — the recipe will appear here',
  'timeline.scriptHint': 'Names in {script}; tap to change',

  // recipe card
  'card.reading': 'Reading the reel',
  'card.arrives': 'Recipe arrives in a few seconds',
  'card.progress': 'Processing progress',
  'card.stages': 'Got it · Listening · Writing',
  'card.failed': 'Couldn’t read this reel',
  'card.linkSafe': 'Your link is safe.',
  'card.private': 'The reel is private or was removed.',
  'card.dropped': 'The connection dropped while we were listening.',
  'card.wrong': 'Something went wrong while we were listening.',
  'card.noRecipe': 'No recipe in this reel',
  'card.noRecipeBody': 'It talks about a dish but never shows how to make it. We kept the link for you.',
  'card.ingredientsOnly': 'Ingredients only — the reel didn’t explain steps',
  'card.taVoice': 'TA voice',
  'card.enVoice': 'EN voice',

  // add reel
  'add.title': 'Add a reel',
  'add.label': 'Paste an Instagram reel link',
  'add.adding': 'Adding…',
  'add.submit': 'Add to my library',
  'add.or': 'or, in Instagram, share any reel to',
  'add.added': 'Got it — reading the reel',

  // search
  'search.label': 'Search recipes',
  'search.back': 'Back',
  'search.clear': 'Clear search',
  'search.also': 'Also searching',
  'search.count_one': '1 recipe',
  'search.count': '{n} recipes',
  'search.idle': 'Search your recipes by dish, ingredient or creator.',
  'search.none': 'Nothing found?',
  'search.tryScripts': 'Try another spelling — thakkali or tomato find the same recipes.',
  'match.dish': 'Matched dish name',
  'match.ingredient': 'Ingredient:',
  'match.transcript': 'What the reel says:',
  'match.caption': 'Caption:',
  'match.tag': 'Tagged “{t}”',
  'match.creator': 'From {t}',
  'match.similar': 'Similar spelling',

  // recipe
  'recipe.notInLibrary': 'This recipe is not in your library.',
  'recipe.share': 'Share recipe',
  'recipe.copied': 'Link copied',
  'recipe.reelLength': '{d} reel',
  'recipe.taVoice': 'Tamil voice',
  'recipe.enVoice': 'English voice',
  'recipe.cookedLast': 'Cooked {n}× · last {date}',
  'recipe.today': 'today',
  'recipe.from': 'Recipe from the reel by',
  'recipe.cookMode': 'Cook mode',
  'recipe.addGrocery': 'Add to grocery list',
  'recipe.inGrocery': 'In your grocery list ✓',
  'recipe.groceryAdded': 'Added to your grocery list',
  'recipe.groceryRemoved': 'Removed from your grocery list',
  'swipe.add': 'Add to grocery',
  'swipe.remove': 'Remove from grocery',
  'grocery.swipeHint': 'Tip: swipe a recipe to the right to add it here.',
  'recipe.playReel': 'Play the reel here',
  'recipe.fullScreen': 'Full screen',
  'recipe.closeReel': 'Close the reel',
  'recipe.addAlbum': 'Add to album',
  'recipe.inAlbums_one': 'In 1 album',
  'recipe.inAlbums': 'In {n} albums',
  'recipe.script': 'Recipe script',
  'recipe.ingredients': 'Ingredients',
  'recipe.inHand': '{n} of {total} in hand',
  'recipe.steps': 'Steps',
  'recipe.tgSteps': 'Steps come in English and Tamil only — showing English.',
  'recipe.noSteps': 'The reel didn’t explain the steps — watch the reel for the method.',
  'recipe.caption': 'Creator’s caption',
  'recipe.says': 'What the reel says',
  'recipe.rough': 'Auto-written from the reel; may be rough in places.',
  'recipe.noRecipe': 'No recipe was found in this reel.',
  'recipe.readingTitle': 'The recipe will appear here in a few seconds',
  'recipe.failedBody': 'Your link is safe. Trying again usually works.',
  'recipe.remove': 'Remove from my library',
  'recipe.removeConfirm': 'Tap again to remove',
  'recipe.removeNote': 'Your notes for this recipe will be deleted too.',
  'recipe.removeKeep': 'The recipe stays available to others who saved it.',
  'albumSheet.title': 'Add to album',
  'albumSheet.none': 'You haven’t made an album yet.',
  'albumSheet.added': 'Added to {name}',
  'albumSheet.removed': 'Removed from {name}',
  'notes.title': 'My kitchen notes',
  'notes.private': 'Only you',
  'notes.cookedToday': 'Cooked today ✓',
  'notes.cookToday': 'I cooked this today',
  'notes.logged_one': 'Logged · 1 time in total · tap again to undo',
  'notes.logged': 'Logged · {n} times in total · tap again to undo',
  'notes.cookedBefore_one': 'Cooked once · last on {date}',
  'notes.cookedBefore': 'Cooked {n} times · last on {date}',
  'notes.notYet': 'Not cooked yet',
  'notes.turnOut': 'How did it turn out?',
  'notes.next': 'Note for next time',
  'notes.placeholder': 'Less chilli, add one more tomato…',
  'notes.save': 'Save note',
  'notes.delete': 'Delete note',
  'notes.today': 'TODAY',

  // cook
  'cook.exit': 'Exit',
  'cook.awake': 'Screen stays on',
  'cook.keepOn': 'Keep your screen on',
  'cook.switch': 'Switch step language',
  'cook.progress': 'Step progress',
  'cook.step': 'STEP',
  'cook.forStep': 'For this step',
  'cook.prev': 'Previous step',
  'cook.next': 'Next step',
  'cook.noSteps': 'This recipe has no steps to cook along with.',
  'cook.backToRecipe': 'Back to recipe',

  // grocery
  'grocery.title': 'Grocery list',
  'grocery.summary': '{n} items · {b} in the bag · from {r} recipes',
  'grocery.summary_one': '{n} items · {b} in the bag · from 1 recipe',
  'grocery.addRecipes': '+ Recipes',
  'grocery.emptyTitle': 'Nothing to buy yet',
  'grocery.emptyBody': 'Open a recipe and tap “Add to grocery list”. Pick a few — the same ingredient from different recipes is merged into one line.',
  'grocery.bringBack': 'Bring back {n} recipes',
  'grocery.bringBack_one': 'Bring back 1 recipe',
  'grocery.goRecipes': 'Go to my recipes',
  'grocery.remove': 'Remove {dish}',
  'grocery.skipped': '{n} recipes have no ingredient list yet.',
  'grocery.skipped_one': '1 recipe has no ingredient list yet.',
  'grocery.hidePantry': 'Hide pantry basics',
  'grocery.hidden': '{n} hidden: {names}',
  'grocery.nothingHidden': 'Nothing to hide',
  'grocery.showingBasics': 'Showing salt, oil and other basics',
  'grocery.vegetables': 'Vegetables',
  'grocery.spices': 'Masala & spices',
  'grocery.others': 'Others',
  'grocery.nRecipes': '{n} recipes',
  'grocery.asNeeded': 'as needed',
  'grocery.copy': 'Copy list',
  'grocery.copied': 'Copied — paste it anywhere',
  'grocery.copyFailed': 'Couldn’t copy on this device',
  'grocery.whatsapp': 'Share on WhatsApp',
  'grocery.later': 'Order from a grocery app — coming later',
  'grocery.textTitle': 'Grocery list — Eat Recipe',

  // settings
  'settings.title': 'Settings',
  'settings.account': 'Your account',
  'settings.via': 'signed in through Instagram DMs',
  'settings.language': 'Language',
  'settings.languageHint': 'Recipes, the app and the bot’s replies.',
  'settings.saved': 'Saved',
  'settings.install': 'Add to your home screen',
  'settings.installBody': 'Opens like an app and stays signed in.',
  'settings.installIOS': 'In Safari, tap Share, then “Add to Home Screen”.',
  'settings.installBtn': 'Add Eat Recipe',
  'install.later': 'Not now',
  'install.android': 'In your browser menu (⋮), tap “Install app” or “Add to Home screen”.',
  'settings.privacy': 'Privacy policy ↗',
  'settings.deletion': 'How your data is deleted ↗',
  'settings.signOut': 'Sign out',
  'settings.delete': 'Delete my account',
  'settings.deleteConfirm': 'Tap again to delete everything',
  'settings.deleteNote': 'Removes your library, notes, albums and cooking history for good.',
  'settings.deleteKeep': 'Recipes others saved stay available to them.',
  // ordering through other apps
  'order.groceryTitle': 'Order on {app}',
  'order.cartTitle': 'Your {app} cart',
  'order.connectTitle': 'Connect your {app} account',
  'order.connectBody': 'You sign in on {app} with your phone number. Eat Recipe then fills your {app} cart from this list; you pay in {app}.',
  'order.connect': 'Connect {app}',
  'order.connectFailed': 'Connecting {app} didn\u2019t finish. Please try again.',
  'order.emptyList': 'Your grocery list is empty.',
  'order.finding': 'Finding your items on {app}\u2026',
  'order.pickHint': 'Pick the pack for each item. Basics you have at home are left out.',
  'order.recipeNeeds': 'recipes need {q}',
  'order.pantry': 'usually at home',
  'order.notFound': 'Not found on {app}',
  'order.pack': 'Pack',
  'order.quantity': 'Quantity',
  'order.less': 'One less',
  'order.more': 'One more',
  'order.build': 'Fill my {app} cart \u00b7 {n} items',
  'order.build_one': 'Fill my {app} cart \u00b7 1 item',
  'order.building': 'Filling your cart\u2026',
  'order.payInApp': 'Prices are {app}\u2019s and may change at checkout. You choose the delivery slot and pay in {app}.',
  'order.openToPay': 'Open {app} to pay',
  'order.cartReady': 'Your cart is ready \u2014 open the {app} app to check out.',
  'order.editItems': 'Change items',
  'food.title': 'Order it on {app}',
  'food.connectBody': 'You sign in on {app} with your phone number. Eat Recipe finds this dish nearby and places the order only after you confirm.',
  'food.deliverTo': 'Deliver to',
  'food.address': 'Address',
  'food.change': 'Change',
  'food.noAddress': 'No saved address on {app}. Add one in the {app} app first.',
  'food.dish': 'Dish name',
  'food.search': 'Search',
  'food.none': 'No restaurant nearby has this on {app} right now.',
  'food.soldOut': 'Sold out',
  'food.customizable': 'Needs options \u2014 order this one in the {app} app',
  'food.review': 'Review order',
  'food.reviewTitle': 'Review your order',
  'food.payWith': 'Pay with',
  'food.noPayment': '{app} offers no payment method for this order here.',
  'food.note': 'Note for the restaurant (optional)',
  'food.notePlaceholder': 'Less spicy, no onion\u2026',
  'food.cancelNote': 'Orders go straight to the restaurant. Cash orders can\u2019t be cancelled here; contact {app} support if needed.',
  'food.choosePayment': 'Choose how to pay',
  'food.place': 'Place order \u00b7 {total} \u00b7 {method}',
  'food.placing': 'Placing your order\u2026',
  'food.doneTitle': 'Your order',
  'food.placed': '{app} order placed',
  'food.orderId': 'Order {id}',
  'food.eta': 'arrives {eta}',
  'food.refresh': 'Check status',
  'food.payNow': 'Pay in your UPI app',
  'food.payBody': 'The order is placed with {app} once the payment goes through.',
  'food.openUPI': 'Open UPI app',
  'food.waiting': 'Waiting for the payment\u2026',
  'food.failed': 'The order didn\u2019t go through',
  'recipe.orderIt': 'Don\u2019t want to cook? Order it on {app}',
  'grocery.orderOn': 'Order on {app}',
  'settings.apps': 'Ordering apps',
  'settings.appsHint': 'Connect an app to fill its cart from your grocery list or order a dish.',
  'settings.connected': 'Connected',
  'settings.connect': 'Connect',
  'settings.disconnect': 'Disconnect',
  'settings.appConnected': '{app} connected',
  'settings.appDisconnected': '{app} disconnected',
  'settings.grocery': 'Groceries',
  'settings.food': 'Food delivery',
  'apps.title': 'Order these groceries',
  'apps.sub': 'From {n} recipes. Choose an app to order from.',
  'apps.sub_one': 'From 1 recipe. Choose an app to order from.',
  'apps.none': 'No grocery app is set up on this server yet.',
  'apps.signIn': 'Sign in with your phone number',
  'apps.note': 'Eat Recipe fills the app\u2019s cart and shows its own prices. Nothing is ordered until you confirm.',
  'soon.badge': 'Coming soon',
  'soon.orderOn': 'Order on {apps}',
  'soon.body': 'Ordering through {app} opens once {app} approves Eat Recipe.',
  'grocery.orderAll': 'Order these groceries',
  'order.connectBody2': 'You sign in on {app} with your phone number. Eat Recipe then builds your cart from this list and shows {app}\u2019s prices; nothing is ordered until you confirm.',
  'order.reviewN': 'Review order \u00b7 {n} items',
  'order.reviewN_one': 'Review order \u00b7 1 item',
  'order.notDeliverable': '{app} can\u2019t deliver this cart to this address right now.',
  'order.realOrder': 'This replaces what is in your {app} cart. Placing the order is real: {app} delivers it and charges the payment method you chose.',
  'order.payTitle': 'Pay to complete the order',
  'order.payNow': 'Open payment page',
  'order.finishInApp': 'Finish in {app} app',
  'order.finishInAppNote': 'Your cart is already in {app}: add more, use coupons and pay there.',
  'order.cancelInApp': 'Need to cancel? Open the {app} app (order {id}). Unpaid orders are not delivered.',
  'order.pickedOf': '{n} of {total} picked',
  'order.basicsLeftOut': 'Basics at home left out',
  'order.include': 'Include {item}',
  'order.otherPacks': 'Other packs',
  'order.itemsFeesNext': '{n} items · fees in the next step',
  'order.itemsFeesNext_one': '1 item · fees in the next step',
  'food.dishesNear': '{n} dishes near you',
  'food.dishesNear_one': '1 dish near you',
  'food.add': 'ADD',
  'food.soldOutShort': 'Sold out',
  'food.optionsInApp': 'Options in {app}',
  'food.oneItem': '{n} items · {total}',
  'food.oneItem_one': '1 item · {total}',
  'order.payInApp2': 'Open the {app} app to complete the payment for this order.',
  'order.checkFirst': 'Before trying again, check your orders in the {app} app so you don\u2019t order twice.',
  // added with the other Indian languages
  'search.placeholder': 'tomato · rasam · thakkali',
  'script.ta': 'Tamil',
  'script.en': 'English',
  'script.tg': 'Tanglish',
  'cook.done': 'Done',
  'recipe.contentLang': 'Recipes are written in Tamil, English and Tanglish.',
  'auth.checking': 'Checking your link…',
  'auth.isYou': 'Is this you?',
  'auth.yourAccount': 'your account',
  'auth.linkFrom': 'This link came from your chat with {h}. It works once, so no one else can open your library with it.',
  'auth.continueAs': 'Continue as {who}',
  'auth.notYou': 'Not you? Get your own link',
  'auth.opening': 'Opening your library…',
  'auth.used': 'This link has already been used',
  'auth.usedBody': 'Each link opens your library once, and only for a short while. Getting a fresh one takes a few seconds.',
  'auth.stepOpen': 'Open your chat with {h}',
  'auth.stepSend': 'Send {w} and tap the new link',
  'auth.newLink': 'Get a new link',
  'auth.safe': 'Your saved recipes are safe — nothing was lost.',
  'auth.checkFailed': 'We couldn’t check your link',
  'auth.checkConn': 'Please check your connection and try again.',
  'auth.hello': 'Hello,',
  'auth.friend': 'friend',
  'auth.summaryEmpty': 'Share a cooking reel to @eat.recipe and it will appear here.',
  'auth.summary': '{n} recipes saved from your DMs.',
  'auth.summary_one': '1 recipe saved from your DMs.',
  'auth.reading': '{n} are still being read.',
  'auth.reading_one': '1 is still being read.',
  'auth.installBody': 'Opens like an app, stays signed in, light on mobile data.',
  'auth.openLibrary': 'Open my library',
  'signin.bubble': 'Here’s your one-time link. Tap to open your library →',
  'signin.title': 'Your recipes are waiting in your DMs',
  'signin.step1': 'Open {h} in Instagram',
  'signin.step2': 'Send the word {w}',
  'signin.step3': 'Tap the link we reply with. No password, ever.',
  'signin.message': 'Message @eat.recipe',
  'signin.have': 'Already have the link? Open it on this phone and you’re in.',
};

export type Key = keyof typeof en;

const ta: Record<Key, string> = {
  'nav.main': 'முதன்மை',
  'nav.home': 'முகப்பு',
  'nav.albums': 'ஆல்பங்கள்',
  'nav.grocery': 'மளிகை',
  'nav.home.label': 'Eat Recipe முகப்பு',
  'common.tryAgain': 'மீண்டும் முயற்சி',
  'common.tryingAgain': 'மீண்டும் முயல்கிறோம்…',
  'common.back': 'பின்செல்',
  'common.cancel': 'ரத்து',
  'common.save': 'சேமி',
  'common.done': 'முடிந்தது',
  'common.loading': 'ஏற்றுகிறது',
  'common.seeAll': 'எல்லாம்',
  'common.addReel': 'ரீல் சேர்',
  'common.profile': 'அமைப்புகள், சுயவிவரம்',
  'common.watchReel': 'ரீலைப் பார் ↗',
  'common.veg': 'சைவம்',
  'common.nonveg': 'அசைவம்',
  'common.unsure': 'தெரியவில்லை',
  'n.recipes_one': '{n} செய்முறை',
  'n.recipes': '{n} செய்முறைகள்',
  'n.ingredients_one': '{n} பொருள்',
  'n.ingredients': '{n} பொருட்கள்',
  'n.steps_one': '{n} படி',
  'n.steps': '{n} படிகள்',
  'n.cooked': '{n} முறை சமைத்தது',
  'n.cookedOn': '{n} முறை சமைத்தது · {date}',
  'verdict.again': 'மீண்டும் செய்யணும்',
  'verdict.ok': 'பரவாயில்லை',
  'verdict.no': 'நமக்கு வேண்டாம்',

  'lang.title': 'உங்கள் மொழியைத் தேர்ந்தெடுங்கள்',
  'lang.body': 'செய்முறைகள், பொத்தான்கள், Instagram பாட்டின் பதில்கள் எல்லாம் இந்த மொழியில் வரும். அமைப்புகளில் எப்போது வேண்டுமானாலும் மாற்றலாம்.',
  'lang.ta': 'செய்முறைகளும் ஆப்பும் தமிழில்',
  'lang.en': 'செய்முறைகளும் ஆப்பும் ஆங்கிலத்தில்',
  'lang.tg': 'thakkali, vengayam போன்ற பெயர்கள் · ஆப் ஆங்கிலத்தில்',
  'lang.continue': 'தொடர்க',
  'lang.saveFailed': 'மொழியைச் சேமிக்க முடியவில்லை. இணைப்பைச் சரிபார்த்து மீண்டும் முயலுங்கள்.',

  'greet.tiffin': 'காலை வணக்கம்{name} · டிஃபன் நேரம்',
  'greet.lunch': 'மதிய வணக்கம்{name} · சாப்பாட்டு நேரம்',
  'greet.teatime': 'மாலை வணக்கம்{name} · டீ நேரம்',
  'greet.dinner': 'மாலை வணக்கம்{name} · இரவு சாப்பாட்டு நேரம்',
  'greet.latenight': 'இரவு வணக்கம்{name} · சட்டுனு ஏதாவது?',
  'home.title': 'இன்னைக்கு என்ன சாப்பிடலாம்?',
  'home.search': '{n} செய்முறைகளில் தேடு · thakkali · ரசம்',
  'home.searchEmpty': 'தேடு · thakkali · ரசம்',
  'home.mood': 'இப்போ என்ன மூடு?',
  'home.surprise': 'முடிவெடுக்க முடியலையா? நாங்க சொல்றோம்',
  'home.howAbout': 'இதைச் செய்யலாமா…',
  'home.cookThis': 'இதைச் சமைப்போம்',
  'home.another': 'வேறொன்று',
  'home.cookAgain': 'மீண்டும் சமைக்க',
  'home.cookAgainHint': '“மீண்டும் செய்யணும்” என்றவை',
  'home.yourAlbums': 'உங்கள் ஆல்பங்கள்',
  'home.justSaved': 'இப்போது சேமித்தவை',
  'home.allByDate': 'தேதி வாரியாக',
  'home.readingFrom': '{who} ரீலைப் படிக்கிறோம்',
  'home.reading': 'ரீலைப் படிக்கிறோம்',
  'home.landsInAlbums': 'தயாரானதும் சரியான ஆல்பங்களில் சேரும்',
  'home.addedTo': '{albums} ஆல்பங்களில் சேர்ந்தது',
  'home.noRecipe': 'இந்த ரீலில் செய்முறை இல்லை',
  'home.failed': 'இந்த ரீலைப் படிக்க முடியவில்லை · மீண்டும் முயல தட்டுங்கள்',
  'home.newTitle': 'உங்கள் செய்முறைகள் இங்கே வரும், ஆசை வாரியாகப் பிரித்து',
  'home.newBody': 'Instagram-இல் எந்தச் சமையல் ரீலிலும் ➤ தட்டி @eat.recipe-க்கு அனுப்புங்கள். ஒரு நிமிடத்தில் இங்கே வரும் — தமிழ், English, Tanglish மூன்றிலும்.',
  'home.openInstagram': '@eat.recipe-க்கு மெசேஜ் செய்',
  'home.pasteLink': 'ரீல் இணைப்பை ஒட்டவும்',
  'reason.never_tried': 'சேமித்தது, இன்னும் செய்யவில்லை',
  'reason.make_again_days': '“மீண்டும் செய்யணும்” என்றீர்கள் · {n} நாள் முன்',
  'reason.make_again_weeks': '“மீண்டும் செய்யணும்” என்றீர்கள் · {n} வாரம் முன்',
  'reason.not_cooked_days': '{n} நாளாகச் செய்யவில்லை',
  'reason.not_cooked_weeks': '{n} வாரமாகச் செய்யவில்லை',
  'reason.meal.tiffin': 'டிஃபனுக்கு ஏற்றது',
  'reason.meal.lunch': 'மதிய சாப்பாட்டுக்கு ஏற்றது',
  'reason.meal.teatime': 'டீயுடன் சூப்பர்',
  'reason.meal.dinner': 'இரவு சாப்பாட்டுக்கு ஏற்றது',
  'reason.meal.latenight': 'இரவில் சட்டுனு செய்ய',
  'surprise.none': 'இங்கே சொல்ல இன்னும் எதுவும் இல்லை.',

  'albums.title': 'ஆல்பங்கள்',
  'albums.sub': '{n} செய்முறைகள், வரும்போதே பிரித்து வைத்தோம்',
  'albums.sub_one': '1 செய்முறை, வரும்போதே பிரித்து வைத்தோம்',
  'albums.search': 'எல்லாச் செய்முறைகளிலும் தேடு',
  'albums.mine': 'நீங்கள் உருவாக்கியவை',
  'albums.new': 'புதிய ஆல்பம்',
  'albums.browse': 'இதன்படி பார்',
  'albums.view': 'ஆல்பப் பார்வை',
  'view.craving': 'ஆசை',
  'view.region': 'ஊர்',
  'view.meal': 'வேளை',
  'view.timeline': 'காலவரிசை',
  'hint.craving': 'மனநிலைக்கு ஏற்ப — காரம், இதம், சட்டுனு, லேசா.',
  'hint.region': 'ஊர் ருசி — செட்டிநாடு சமையலறை முதல் உலக உணவு வரை.',
  'hint.meal': 'எந்த வேளை? டிஃபன் முதல் இரவு வரை, லஞ்ச் பாக்ஸ் உட்பட.',
  'hint.timeline': 'நீங்கள் பகிர்ந்த ஒவ்வொரு ரீலும், சேமித்த நாள் வாரியாக.',
  'albums.empty': 'இங்கே இன்னும் எதுவும் இல்லை. சில ரீல்களை @eat.recipe-க்கு அனுப்புங்கள்; அவை தானாகவே ஆல்பங்களில் சேரும்.',
  'tl.week': 'இந்த வாரம்',
  'tl.month_rest': 'இந்த மாதம் முன்னதாக',
  'tl.before': 'அதற்கு முன்',
  'tl.reading': '{n} படிக்கப்படுகிறது…',

  'kind.craving': 'ஆசை',
  'kind.region': 'ஊர்',
  'kind.meal': 'வேளை',
  'kind.mine': 'உங்கள் ஆல்பம்',
  'album.stats': '{n} செய்முறைகள் · {c} சமைத்தவை · {u} இன்னும் செய்யாதவை',
  'album.stats_one': '1 செய்முறை · {c} சமைத்தவை · {u} இன்னும் செய்யாதவை',
  'album.pin': 'முகப்பில் பொருத்து',
  'album.unpin': 'முகப்பிலிருந்து எடு',
  'album.pinned': 'முகப்பில் பொருத்தப்பட்டது',
  'album.unpinned': 'முகப்பிலிருந்து எடுக்கப்பட்டது',
  'album.backToAlbums': 'ஆல்பங்களுக்குத் திரும்பு',
  'filter.label': 'செய்முறைகளை வடிகட்டு',
  'filter.all': 'எல்லாம்',
  'filter.untried': 'இன்னும் செய்யாதவை',
  'album.pick': 'இதிலிருந்து எனக்கு ஒன்றைச் சொல்',
  'album.favourites': 'உங்கள் விருப்பங்கள்',
  'album.notTried': 'இன்னும் செய்யாதவை',
  'album.cookedBefore': 'முன்பு சமைத்தவை',
  'album.saved': 'சேமித்தது {date}',
  'album.shop': 'இவற்றில் சிலவற்றுக்கு மளிகை வாங்க',
  'album.shopTitle': 'எவற்றைச் சமைக்கப் போகிறீர்கள்?',
  'album.shopGo': 'மளிகைப் பட்டியல் · {n}',
  'album.empty': 'இங்கே இன்னும் செய்முறை இல்லை.',
  'album.emptyMine': 'ஒரு செய்முறையைத் திறந்து “ஆல்பத்தில் சேர்” தட்டினால் இங்கே வரும்.',
  'album.noMatch': 'இந்த வடிகட்டலுக்கு எதுவும் இல்லை.',
  'album.showAll': 'எல்லாம் காட்டு',
  'album.rename': 'பெயர் மாற்று',
  'album.delete': 'ஆல்பத்தை நீக்கு',
  'album.deleteConfirm': 'நீக்க மீண்டும் தட்டுங்கள் — செய்முறைகள் உங்கள் தொகுப்பில் இருக்கும்',
  'album.notFound': 'இந்த ஆல்பம் இப்போது இல்லை.',
  'album.nameLabel': 'ஆல்பத்தின் பெயர்',
  'album.namePlaceholder': 'அம்மா ஸ்டைல் · ஞாயிறு ஸ்பெஷல்',
  'album.create': 'ஆல்பம் உருவாக்கு',
  'album.created': 'ஆல்பம் உருவானது',

  'timeline.title': 'தேதி வாரியாக',
  'timeline.newest': 'புதியவை முதலில்',
  'timeline.search': 'செய்முறைகளைத் தேடு',
  'timeline.empty': 'உங்கள் தொகுப்பு காலியாக உள்ளது',
  'timeline.emptyBody': 'Instagram-இல் எந்தச் சமையல் ரீலிலும் ➤ தட்டி @eat.recipe-க்கு அனுப்புங்கள். சில நொடிகளில் செய்முறை இங்கே வரும் — தமிழ், English, Tanglish மூன்றிலும்.',
  'timeline.noMatch': 'இந்த வடிகட்டலுக்கு எதுவும் இல்லை.',
  'timeline.showAll': 'எல்லாம் காட்டு',
  'timeline.retrying': 'மீண்டும் முயல்கிறோம் — செய்முறை இங்கே வரும்',
  'timeline.scriptHint': 'பெயர்கள் {script}-இல்; மாற்ற தட்டுங்கள்',

  'card.reading': 'ரீலைப் படிக்கிறோம்',
  'card.arrives': 'சில நொடிகளில் செய்முறை வரும்',
  'card.progress': 'செயலாக்க நிலை',
  'card.stages': 'கிடைத்தது · கேட்கிறோம் · எழுதுகிறோம்',
  'card.failed': 'இந்த ரீலைப் படிக்க முடியவில்லை',
  'card.linkSafe': 'உங்கள் இணைப்பு பத்திரமாக உள்ளது.',
  'card.private': 'ரீல் தனிப்பட்டது அல்லது நீக்கப்பட்டது.',
  'card.dropped': 'கேட்கும்போது இணைப்பு துண்டிக்கப்பட்டது.',
  'card.wrong': 'கேட்கும்போது ஏதோ தவறு நடந்தது.',
  'card.noRecipe': 'இந்த ரீலில் செய்முறை இல்லை',
  'card.noRecipeBody': 'ஒரு உணவைப் பற்றிப் பேசுகிறது, ஆனால் செய்முறையைக் காட்டவில்லை. இணைப்பை வைத்திருக்கிறோம்.',
  'card.ingredientsOnly': 'பொருட்கள் மட்டும் — ரீல் படிகளைச் சொல்லவில்லை',
  'card.taVoice': 'தமிழ் குரல்',
  'card.enVoice': 'EN குரல்',

  'add.title': 'ரீல் சேர்',
  'add.label': 'Instagram ரீல் இணைப்பை ஒட்டவும்',
  'add.adding': 'சேர்க்கிறோம்…',
  'add.submit': 'என் தொகுப்பில் சேர்',
  'add.or': 'அல்லது Instagram-இல் எந்த ரீலையும் இதற்குப் பகிருங்கள்',
  'add.added': 'கிடைத்தது — ரீலைப் படிக்கிறோம்',

  'search.label': 'செய்முறைகளைத் தேடு',
  'search.back': 'பின்செல்',
  'search.clear': 'தேடலை அழி',
  'search.also': 'இதையும் தேடுகிறோம்',
  'search.count_one': '1 செய்முறை',
  'search.count': '{n} செய்முறைகள்',
  'search.idle': 'உணவு, பொருள் அல்லது கிரியேட்டர் பெயரால் தேடுங்கள்.',
  'search.none': 'எதுவும் கிடைக்கவில்லையா?',
  'search.tryScripts': 'வேறு எழுத்தில் முயலுங்கள் — தக்காளி, thakkali, tomato மூன்றும் ஒரே செய்முறைகளைக் காட்டும்.',
  'match.dish': 'உணவின் பெயரில் பொருந்தியது',
  'match.ingredient': 'பொருள்:',
  'match.transcript': 'ரீலில் சொன்னது:',
  'match.caption': 'கேப்ஷன்:',
  'match.tag': '“{t}” குறிச்சொல்',
  'match.creator': '{t} இடமிருந்து',
  'match.similar': 'ஒத்த எழுத்துக்கூட்டல்',

  'recipe.notInLibrary': 'இந்தச் செய்முறை உங்கள் தொகுப்பில் இல்லை.',
  'recipe.share': 'செய்முறையைப் பகிர்',
  'recipe.copied': 'இணைப்பு நகலெடுக்கப்பட்டது',
  'recipe.reelLength': '{d} ரீல்',
  'recipe.taVoice': 'தமிழ் குரல்',
  'recipe.enVoice': 'ஆங்கிலக் குரல்',
  'recipe.cookedLast': '{n} முறை சமைத்தது · கடைசியாக {date}',
  'recipe.today': 'இன்று',
  'recipe.from': 'இந்த ரீலின் செய்முறை',
  'recipe.cookMode': 'சமையல் முறை',
  'recipe.addGrocery': 'மளிகைப் பட்டியலில் சேர்',
  'recipe.inGrocery': 'மளிகைப் பட்டியலில் உள்ளது ✓',
  'recipe.groceryAdded': 'மளிகைப் பட்டியலில் சேர்க்கப்பட்டது',
  'recipe.groceryRemoved': 'மளிகைப் பட்டியலிலிருந்து நீக்கப்பட்டது',
  'swipe.add': 'மளிகையில் சேர்',
  'swipe.remove': 'மளிகையிலிருந்து நீக்கு',
  'grocery.swipeHint': 'குறிப்பு: ஒரு செய்முறையை வலப்பக்கம் தள்ளினால் இங்கே சேரும்.',
  'recipe.playReel': 'ரீலை இங்கே இயக்கு',
  'recipe.fullScreen': 'முழுத் திரை',
  'recipe.closeReel': 'ரீலை மூடு',
  'recipe.addAlbum': 'ஆல்பத்தில் சேர்',
  'recipe.inAlbums_one': '1 ஆல்பத்தில் உள்ளது',
  'recipe.inAlbums': '{n} ஆல்பங்களில் உள்ளது',
  'recipe.script': 'செய்முறை எழுத்து',
  'recipe.ingredients': 'பொருட்கள்',
  'recipe.inHand': '{total}-இல் {n} கையில்',
  'recipe.steps': 'செய்முறை',
  'recipe.tgSteps': 'படிகள் ஆங்கிலம், தமிழில் மட்டுமே — ஆங்கிலத்தில் காட்டுகிறோம்.',
  'recipe.noSteps': 'ரீல் படிகளைச் சொல்லவில்லை — செய்முறைக்கு ரீலைப் பாருங்கள்.',
  'recipe.caption': 'கிரியேட்டரின் கேப்ஷன்',
  'recipe.says': 'ரீலில் சொன்னது',
  'recipe.rough': 'ரீலிலிருந்து தானாக எழுதியது; சில இடங்களில் பிழை இருக்கலாம்.',
  'recipe.noRecipe': 'இந்த ரீலில் செய்முறை கிடைக்கவில்லை.',
  'recipe.readingTitle': 'சில நொடிகளில் செய்முறை இங்கே வரும்',
  'recipe.failedBody': 'உங்கள் இணைப்பு பத்திரமாக உள்ளது. மீண்டும் முயன்றால் பெரும்பாலும் வேலை செய்யும்.',
  'recipe.remove': 'என் தொகுப்பிலிருந்து நீக்கு',
  'recipe.removeConfirm': 'நீக்க மீண்டும் தட்டுங்கள்',
  'recipe.removeNote': 'இந்தச் செய்முறைக்கான உங்கள் குறிப்புகளும் நீக்கப்படும்.',
  'recipe.removeKeep': 'இதைச் சேமித்த மற்றவர்களுக்குச் செய்முறை இருக்கும்.',
  'albumSheet.title': 'ஆல்பத்தில் சேர்',
  'albumSheet.none': 'நீங்கள் இன்னும் ஆல்பம் உருவாக்கவில்லை.',
  'albumSheet.added': '{name} ஆல்பத்தில் சேர்க்கப்பட்டது',
  'albumSheet.removed': '{name} ஆல்பத்திலிருந்து நீக்கப்பட்டது',
  'notes.title': 'என் சமையல் குறிப்புகள்',
  'notes.private': 'உங்களுக்கு மட்டும்',
  'notes.cookedToday': 'இன்று சமைத்தது ✓',
  'notes.cookToday': 'இன்று இதைச் சமைத்தேன்',
  'notes.logged_one': 'பதிவானது · மொத்தம் 1 முறை · திரும்பப் பெற மீண்டும் தட்டுங்கள்',
  'notes.logged': 'பதிவானது · மொத்தம் {n} முறை · திரும்பப் பெற மீண்டும் தட்டுங்கள்',
  'notes.cookedBefore_one': 'ஒருமுறை சமைத்தது · கடைசியாக {date}',
  'notes.cookedBefore': '{n} முறை சமைத்தது · கடைசியாக {date}',
  'notes.notYet': 'இன்னும் சமைக்கவில்லை',
  'notes.turnOut': 'எப்படி வந்தது?',
  'notes.next': 'அடுத்த முறைக்கான குறிப்பு',
  'notes.placeholder': 'காரம் குறைவாக, இன்னொரு தக்காளி…',
  'notes.save': 'குறிப்பைச் சேமி',
  'notes.delete': 'குறிப்பை நீக்கு',
  'notes.today': 'இன்று',

  'cook.exit': 'வெளியேறு',
  'cook.awake': 'திரை அணையாது',
  'cook.keepOn': 'திரையை அணைக்காமல் வையுங்கள்',
  'cook.switch': 'படி மொழியை மாற்று',
  'cook.progress': 'படி நிலை',
  'cook.step': 'படி',
  'cook.forStep': 'இந்த படிக்கு தேவை',
  'cook.prev': 'முந்தைய படி',
  'cook.next': 'அடுத்து',
  'cook.noSteps': 'இந்தச் செய்முறையில் பின்பற்ற படிகள் இல்லை.',
  'cook.backToRecipe': 'செய்முறைக்குத் திரும்பு',

  'grocery.title': 'மளிகைப் பட்டியல்',
  'grocery.summary': '{n} பொருட்கள் · {b} பையில் · {r} செய்முறைகளிலிருந்து',
  'grocery.summary_one': '{n} பொருட்கள் · {b} பையில் · 1 செய்முறையிலிருந்து',
  'grocery.addRecipes': '+ செய்முறைகள்',
  'grocery.emptyTitle': 'இன்னும் வாங்க எதுவும் இல்லை',
  'grocery.emptyBody': 'ஒரு செய்முறையைத் திறந்து “மளிகைப் பட்டியலில் சேர்” தட்டுங்கள். சிலவற்றைத் தேர்ந்தெடுங்கள் — வெவ்வேறு செய்முறைகளில் உள்ள ஒரே பொருள் ஒரே வரியாகச் சேரும்.',
  'grocery.bringBack': '{n} செய்முறைகளைத் திரும்பக் கொண்டு வா',
  'grocery.bringBack_one': '1 செய்முறையைத் திரும்பக் கொண்டு வா',
  'grocery.goRecipes': 'என் செய்முறைகளுக்குச் செல்',
  'grocery.remove': '{dish} நீக்கு',
  'grocery.skipped': '{n} செய்முறைகளுக்கு இன்னும் பொருட்பட்டியல் இல்லை.',
  'grocery.skipped_one': '1 செய்முறைக்கு இன்னும் பொருட்பட்டியல் இல்லை.',
  'grocery.hidePantry': 'வீட்டில் உள்ள அடிப்படைகளை மறை',
  'grocery.hidden': '{n} மறைக்கப்பட்டது: {names}',
  'grocery.nothingHidden': 'மறைக்க எதுவும் இல்லை',
  'grocery.showingBasics': 'உப்பு, எண்ணெய் போன்றவற்றையும் காட்டுகிறோம்',
  'grocery.vegetables': 'காய்கறி',
  'grocery.spices': 'மசாலா',
  'grocery.others': 'மற்றவை',
  'grocery.nRecipes': '{n} செய்முறைகள்',
  'grocery.asNeeded': 'தேவைக்கு',
  'grocery.copy': 'பட்டியலை நகலெடு',
  'grocery.copied': 'நகலெடுத்தது — எங்கு வேண்டுமானாலும் ஒட்டலாம்',
  'grocery.copyFailed': 'இந்தச் சாதனத்தில் நகலெடுக்க முடியவில்லை',
  'grocery.whatsapp': 'WhatsApp-இல் பகிர்',
  'grocery.later': 'மளிகை ஆப்பில் ஆர்டர் — விரைவில்',
  'grocery.textTitle': 'மளிகைப் பட்டியல் — Eat Recipe',

  'settings.title': 'அமைப்புகள்',
  'settings.account': 'உங்கள் கணக்கு',
  'settings.via': 'Instagram DM மூலம் உள்நுழைந்தீர்கள்',
  'settings.language': 'மொழி',
  'settings.languageHint': 'செய்முறைகள், ஆப், பாட்டின் பதில்கள்.',
  'settings.saved': 'சேமிக்கப்பட்டது',
  'settings.install': 'முகப்புத் திரையில் சேர்',
  'settings.installBody': 'ஆப் போலத் திறக்கும், உள்நுழைந்தே இருக்கும்.',
  'settings.installIOS': 'Safari-இல் Share தட்டி, “Add to Home Screen” தேர்ந்தெடுங்கள்.',
  'settings.installBtn': 'Eat Recipe சேர்',
  'install.later': 'இப்போது வேண்டாம்',
  'install.android': 'உலாவி மெனுவில் (⋮) “Install app” அல்லது “Add to Home screen” தட்டுங்கள்.',
  'settings.privacy': 'தனியுரிமைக் கொள்கை ↗',
  'settings.deletion': 'உங்கள் தரவு எப்படி நீக்கப்படுகிறது ↗',
  'settings.signOut': 'வெளியேறு',
  'settings.delete': 'என் கணக்கை நீக்கு',
  'settings.deleteConfirm': 'எல்லாவற்றையும் நீக்க மீண்டும் தட்டுங்கள்',
  'settings.deleteNote': 'உங்கள் தொகுப்பு, குறிப்புகள், ஆல்பங்கள், சமையல் வரலாறு நிரந்தரமாக நீக்கப்படும்.',
  'settings.deleteKeep': 'மற்றவர்கள் சேமித்த செய்முறைகள் அவர்களுக்கு இருக்கும்.',
  'order.groceryTitle': '{app}-இல் ஆர்டர் செய்',
  'order.cartTitle': 'உங்கள் {app} கார்ட்',
  'order.connectTitle': 'உங்கள் {app} கணக்கை இணைக்கவும்',
  'order.connectBody': '{app}-இல் உங்கள் போன் நம்பரால் உள்நுழைவீர்கள். பிறகு இந்தப் பட்டியலிலிருந்து Eat Recipe உங்கள் {app} கார்ட்டை நிரப்பும்; பணம் {app}-இல் செலுத்துவீர்கள்.',
  'order.connect': '{app} இணை',
  'order.connectFailed': '{app} இணைப்பு முடியவில்லை. மீண்டும் முயலுங்கள்.',
  'order.emptyList': 'உங்கள் மளிகைப் பட்டியல் காலியாக உள்ளது.',
  'order.finding': '{app}-இல் உங்கள் பொருட்களைத் தேடுகிறோம்…',
  'order.pickHint': 'ஒவ்வொரு பொருளுக்கும் பேக்கைத் தேர்ந்தெடுங்கள். வீட்டில் உள்ள அடிப்படைகள் விடப்பட்டுள்ளன.',
  'order.recipeNeeds': 'செய்முறைக்கு {q}',
  'order.pantry': 'பொதுவாக வீட்டில் இருக்கும்',
  'order.notFound': '{app}-இல் கிடைக்கவில்லை',
  'order.pack': 'பேக்',
  'order.quantity': 'எண்ணிக்கை',
  'order.less': 'ஒன்று குறை',
  'order.more': 'ஒன்று கூட்டு',
  'order.build': 'என் {app} கார்ட்டை நிரப்பு · {n} பொருட்கள்',
  'order.build_one': 'என் {app} கார்ட்டை நிரப்பு · 1 பொருள்',
  'order.building': 'கார்ட்டை நிரப்புகிறோம்…',
  'order.payInApp': 'விலைகள் {app}-உடையவை, செக்அவுட்டில் மாறலாம். டெலிவரி நேரத்தைத் தேர்ந்தெடுத்து {app}-இல் பணம் செலுத்துவீர்கள்.',
  'order.openToPay': 'பணம் செலுத்த {app} திற',
  'order.cartReady': 'உங்கள் கார்ட் தயார் — செக்அவுட் செய்ய {app} ஆப்பைத் திறங்கள்.',
  'order.editItems': 'பொருட்களை மாற்று',
  'food.title': '{app}-இல் ஆர்டர் செய்',
  'food.connectBody': '{app}-இல் உங்கள் போன் நம்பரால் உள்நுழைவீர்கள். Eat Recipe இந்த உணவை அருகில் தேடி, நீங்கள் உறுதி செய்த பிறகே ஆர்டர் செய்யும்.',
  'food.deliverTo': 'டெலிவரி முகவரி',
  'food.address': 'முகவரி',
  'food.change': 'மாற்று',
  'food.noAddress': '{app}-இல் சேமித்த முகவரி இல்லை. முதலில் {app} ஆப்பில் ஒன்றைச் சேர்க்கவும்.',
  'food.dish': 'உணவின் பெயர்',
  'food.search': 'தேடு',
  'food.none': 'இப்போது அருகில் எந்த உணவகத்திலும் {app}-இல் இது இல்லை.',
  'food.soldOut': 'தீர்ந்துவிட்டது',
  'food.customizable': 'விருப்பங்கள் தேவை — இதை {app} ஆப்பில் ஆர்டர் செய்யுங்கள்',
  'food.review': 'ஆர்டரைப் பார்',
  'food.reviewTitle': 'உங்கள் ஆர்டரைச் சரிபாருங்கள்',
  'food.payWith': 'பணம் செலுத்தும் முறை',
  'food.noPayment': 'இந்த ஆர்டருக்கு {app} இங்கே பணம் செலுத்தும் முறை எதையும் தரவில்லை.',
  'food.note': 'உணவகத்துக்கு குறிப்பு (விருப்பம்)',
  'food.notePlaceholder': 'காரம் குறைவாக, வெங்காயம் வேண்டாம்…',
  'food.cancelNote': 'ஆர்டர் நேரடியாக உணவகத்துக்குப் போகும். கேஷ் ஆர்டர்களை இங்கே ரத்து செய்ய முடியாது; தேவையெனில் {app} உதவி மையத்தைத் தொடர்புகொள்ளுங்கள்.',
  'food.choosePayment': 'பணம் செலுத்தும் முறையைத் தேர்ந்தெடுங்கள்',
  'food.place': 'ஆர்டர் செய் · {total} · {method}',
  'food.placing': 'ஆர்டர் செய்கிறோம்…',
  'food.doneTitle': 'உங்கள் ஆர்டர்',
  'food.placed': '{app} ஆர்டர் செய்யப்பட்டது',
  'food.orderId': 'ஆர்டர் {id}',
  'food.eta': '{eta}-இல் வரும்',
  'food.refresh': 'நிலையைப் பார்',
  'food.payNow': 'உங்கள் UPI ஆப்பில் பணம் செலுத்துங்கள்',
  'food.payBody': 'பணம் சென்றதும் {app}-இல் ஆர்டர் உறுதியாகும்.',
  'food.openUPI': 'UPI ஆப்பைத் திற',
  'food.waiting': 'பணத்துக்காகக் காத்திருக்கிறோம்…',
  'food.failed': 'ஆர்டர் நடக்கவில்லை',
  'recipe.orderIt': 'சமைக்க விருப்பமில்லையா? {app}-இல் ஆர்டர் செய்',
  'grocery.orderOn': '{app}-இல் ஆர்டர் செய்',
  'settings.apps': 'ஆர்டர் ஆப்புகள்',
  'settings.appsHint': 'மளிகைப் பட்டியலிலிருந்து கார்ட்டை நிரப்ப அல்லது உணவை ஆர்டர் செய்ய ஒரு ஆப்பை இணைக்கவும்.',
  'settings.connected': 'இணைக்கப்பட்டது',
  'settings.connect': 'இணை',
  'settings.disconnect': 'துண்டி',
  'settings.appConnected': '{app} இணைக்கப்பட்டது',
  'settings.appDisconnected': '{app} துண்டிக்கப்பட்டது',
  'settings.grocery': 'மளிகை',
  'settings.food': 'உணவு டெலிவரி',
  'apps.title': 'இந்த மளிகையை ஆர்டர் செய்',
  'apps.sub': '{n} செய்முறைகளிலிருந்து. எந்த ஆப்பில் ஆர்டர் செய்வது?',
  'apps.sub_one': '1 செய்முறையிலிருந்து. எந்த ஆப்பில் ஆர்டர் செய்வது?',
  'apps.none': 'இந்த சர்வரில் இன்னும் மளிகை ஆப் அமைக்கப்படவில்லை.',
  'apps.signIn': 'உங்கள் போன் நம்பரால் உள்நுழையுங்கள்',
  'apps.note': 'Eat Recipe ஆப்பின் கார்ட்டை நிரப்பி, அதன் சொந்த விலைகளைக் காட்டும். நீங்கள் உறுதி செய்யும் வரை எதுவும் ஆர்டர் ஆகாது.',
  'soon.badge': 'விரைவில்',
  'soon.orderOn': '{apps}-இல் ஆர்டர்',
  'soon.body': '{app} Eat Recipe-ஐ அனுமதித்ததும் இங்கிருந்து ஆர்டர் செய்யலாம்.',
  'grocery.orderAll': 'இந்த மளிகையை ஆர்டர் செய்',
  'order.connectBody2': '{app}-இல் உங்கள் போன் நம்பரால் உள்நுழைவீர்கள். பிறகு Eat Recipe இந்தப் பட்டியலிலிருந்து கார்ட்டை உருவாக்கி {app} விலைகளைக் காட்டும்; நீங்கள் உறுதி செய்யும் வரை எதுவும் ஆர்டர் ஆகாது.',
  'order.reviewN': 'ஆர்டரைப் பார் · {n} பொருட்கள்',
  'order.reviewN_one': 'ஆர்டரைப் பார் · 1 பொருள்',
  'order.notDeliverable': 'இப்போது இந்த முகவரிக்கு {app} இந்தக் கார்ட்டை டெலிவரி செய்ய முடியாது.',
  'order.realOrder': 'இது உங்கள் {app} கார்ட்டில் உள்ளதை மாற்றும். ஆர்டர் உண்மையானது: {app} டெலிவரி செய்து, நீங்கள் தேர்ந்த முறையில் பணம் பெறும்.',
  'order.payTitle': 'ஆர்டரை முடிக்க பணம் செலுத்துங்கள்',
  'order.payNow': 'பணம் செலுத்தும் பக்கத்தைத் திற',
  'order.finishInApp': '{app} ஆப்பில் முடிக்கவும்',
  'order.finishInAppNote': 'உங்கள் கார்ட் ஏற்கனவே {app}-இல் உள்ளது: மேலும் சேர்க்கலாம், கூப்பன் பயன்படுத்தி அங்கே பணம் செலுத்தலாம்.',
  'order.cancelInApp': 'ரத்து செய்ய வேண்டுமா? {app} ஆப்பைத் திறங்கள் (ஆர்டர் {id}). பணம் செலுத்தாத ஆர்டர்கள் டெலிவரி ஆகாது.',
  'order.pickedOf': '{total}-இல் {n} தேர்ந்தவை',
  'order.basicsLeftOut': 'வீட்டில் உள்ளவை விடப்பட்டன',
  'order.include': '{item} சேர்',
  'order.otherPacks': 'மற்ற பேக்குகள்',
  'order.itemsFeesNext': '{n} பொருட்கள் · கட்டணங்கள் அடுத்த படியில்',
  'order.itemsFeesNext_one': '1 பொருள் · கட்டணங்கள் அடுத்த படியில்',
  'food.dishesNear': 'அருகில் {n} உணவுகள்',
  'food.dishesNear_one': 'அருகில் 1 உணவு',
  'food.add': 'சேர்',
  'food.soldOutShort': 'தீர்ந்தது',
  'food.optionsInApp': '{app}-இல் விருப்பங்கள்',
  'food.oneItem': '{n} உணவுகள் · {total}',
  'food.oneItem_one': '1 உணவு · {total}',
  'order.payInApp2': 'இந்த ஆர்டருக்கான பணத்தை செலுத்த {app} ஆப்பைத் திறங்கள்.',
  'order.checkFirst': 'மீண்டும் முயலும் முன், இரண்டு முறை ஆர்டர் ஆகாமல் இருக்க {app} ஆப்பில் உங்கள் ஆர்டர்களைப் பாருங்கள்.',
  'search.placeholder': 'தக்காளி · ரசம் · thakkali',
  'script.ta': 'தமிழ்',
  'script.en': 'ஆங்கிலம்',
  'script.tg': 'Tanglish',
  'cook.done': 'முடிந்தது',
  'recipe.contentLang': 'செய்முறைகள் தமிழ், ஆங்கிலம், Tanglish-இல் எழுதப்படுகின்றன.',
  'auth.checking': 'உங்கள் இணைப்பைச் சரிபார்க்கிறோம்…',
  'auth.isYou': 'இது நீங்கள் தானா?',
  'auth.yourAccount': 'உங்கள் கணக்கு',
  'auth.linkFrom': 'இந்த இணைப்பு {h} உடனான உங்கள் உரையாடலில் இருந்து வந்தது. ஒருமுறை மட்டுமே வேலை செய்யும், அதனால் வேறு யாரும் இதைக் கொண்டு உங்கள் தொகுப்பைத் திறக்க முடியாது.',
  'auth.continueAs': '{who} ஆகத் தொடர்க',
  'auth.notYou': 'நீங்கள் இல்லையா? உங்களுக்கான இணைப்பைப் பெறுங்கள்',
  'auth.opening': 'உங்கள் ரெசிபிகளைத் திறக்கிறோம்…',
  'auth.used': 'இந்த இணைப்பு ஏற்கனவே பயன்படுத்தப்பட்டது',
  'auth.usedBody': 'ஒவ்வொரு இணைப்பும் உங்கள் தொகுப்பை ஒருமுறை, சிறிது நேரம் மட்டுமே திறக்கும். புதிய இணைப்பு சில வினாடிகளில் கிடைக்கும்.',
  'auth.stepOpen': '{h} உடனான உரையாடலைத் திறங்கள்',
  'auth.stepSend': '{w} என்று அனுப்பி, புதிய இணைப்பைத் தட்டுங்கள்',
  'auth.newLink': 'புதிய இணைப்பு பெறு',
  'auth.safe': 'சேமித்த செய்முறைகள் பத்திரமாக உள்ளன — எதுவும் இழக்கப்படவில்லை.',
  'auth.checkFailed': 'உங்கள் இணைப்பைச் சரிபார்க்க முடியவில்லை',
  'auth.checkConn': 'இணைய இணைப்பைச் சரிபார்த்து மீண்டும் முயலுங்கள்.',
  'auth.hello': 'வணக்கம்,',
  'auth.friend': 'நண்பரே',
  'auth.summaryEmpty': 'ஒரு சமையல் ரீலை @eat.recipe-க்கு அனுப்புங்கள், அது இங்கே வரும்.',
  'auth.summary': 'உங்கள் DM-இல் இருந்து {n} செய்முறைகள் சேமிக்கப்பட்டன.',
  'auth.summary_one': 'உங்கள் DM-இல் இருந்து 1 செய்முறை சேமிக்கப்பட்டது.',
  'auth.reading': '{n} இன்னும் படிக்கப்படுகின்றன.',
  'auth.reading_one': '1 இன்னும் படிக்கப்படுகிறது.',
  'auth.installBody': 'ஆப் போலத் திறக்கும், உள்நுழைந்தபடியே இருக்கும், குறைந்த டேட்டா.',
  'auth.openLibrary': 'என் செய்முறைகளைத் திற',
  'signin.bubble': 'இதோ உங்கள் ஒருமுறை இணைப்பு. தட்டித் திறங்கள் →',
  'signin.title': 'உங்கள் ரெசிபிகள் DM-இல் காத்திருக்கின்றன',
  'signin.step1': 'Instagram-இல் {h} ஐத் திறங்கள்',
  'signin.step2': '{w} என்று அனுப்புங்கள்',
  'signin.step3': 'நாங்கள் அனுப்பும் இணைப்பைத் தட்டுங்கள். கடவுச்சொல் தேவையில்லை.',
  'signin.message': '@eat.recipe-க்கு மெசேஜ் செய்',
  'signin.have': 'இணைப்பு ஏற்கனவே உள்ளதா? இந்த போனில் திறந்தால் போதும்.',
};

const DICT: Record<UI, Record<Key, string>> = { en, ta, hi, te, kn, ml };

export type Vars = Record<string, string | number>;

/** Translates key, filling {placeholders}. With vars.n === 1 a "<key>_one" form is used when present. */
export function t(key: Key, vars: Vars = {}): string {
  const d = DICT[uiLang()];
  const one = `${key}_one` as Key;
  let s = vars.n === 1 && one in d ? d[one] : d[key];
  for (const [k, v] of Object.entries(vars)) s = s.split(`{${k}}`).join(String(v));
  return s;
}

/** Re-renders the component when the language changes and returns the translator. */
export function useT(): typeof t {
  const [, bump] = useState(0);
  useEffect(() => {
    const fn = () => bump((n) => n + 1);
    listeners.add(fn);
    return () => void listeners.delete(fn);
  }, []);
  return t;
}

/** "12 Oct" / "12 அக்." from a YYYY-MM-DD… string, in the UI language. */
export function shortDateL(iso: string | null | undefined): string {
  const m = iso ? /^(\d{4})-(\d{2})-(\d{2})/.exec(iso) : null;
  if (!m) return '';
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  try {
    return new Intl.DateTimeFormat(locale(), { day: 'numeric', month: 'short' }).format(d);
  } catch {
    return `${Number(m[3])}/${Number(m[2])}`;
  }
}

/** "September" / "செப்டம்பர்" (with the year when it isn't this year) for a YYYY-MM month. */
export function monthName(ym: string): string {
  const [y, mo] = ym.split('-').map(Number);
  const opts: Intl.DateTimeFormatOptions = { month: 'long' };
  if (y !== new Date().getFullYear()) opts.year = 'numeric';
  try {
    return new Intl.DateTimeFormat(locale(), opts).format(new Date(y, mo - 1, 1));
  } catch {
    return ym;
  }
}
